import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Flame, Trophy, Target, Zap, ArrowRight, CheckCircle2, Lock, ClipboardList, CalendarClock, AlertTriangle, Clock, Award, Copy, ExternalLink, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
        <Icon className={`h-4 w-4 ${accent}`} />
      </div>
      <div className="mt-3 font-display font-bold text-3xl tracking-tight">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [ach, setAch] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [azCert, setAzCert] = useState(null);
  const [lb, setLb] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/achievements").then((r) => setAch(r.data)).catch(() => {});
    api.get("/my/assignments").then((r) => setAssignments(r.data.assignments)).catch(() => {});
    api.get("/guided/certificate").then((r) => setAzCert(r.data)).catch(() => {});
    api.get("/azure/leaderboard").then((r) => setLb(r.data)).catch(() => {});
  }, []);

  if (!d) return <div className="p-10 text-muted-foreground">Loading dashboard…</div>;

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-sm text-muted-foreground">Welcome back,</div>
          <h1 className="font-display font-bold text-3xl tracking-tight">{user?.name?.split(" ")[0] || "Student"} 👋</h1>
        </div>
        <Badge variant="outline" className="text-xs uppercase tracking-widest">Skill Level {d.level}</Badge>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Stat icon={Zap} label="Total XP" value={d.xp} accent="text-primary" />
        <Stat icon={Flame} label="Day Streak" value={d.streak} accent="text-orange-500" />
        <Stat icon={Target} label="Labs Done" value={`${d.completed_count}/${d.total_labs}`} accent="text-success" />
        <Stat icon={Trophy} label="Badges" value={d.badges.length} accent="text-yellow-500" />
      </div>

      {/* Linux Hands-On Lab program achievement summary */}
      {ach && (
        <div className="rounded-xl border border-border bg-card p-6 mt-6" data-testid="dashboard-achievement-summary">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Linux Hands-On Lab Progress</div>
              <div className="font-display font-bold text-2xl mt-1">{ach.stats.completed} of {ach.stats.required} Labs Completed</div>
              <div className="text-sm text-muted-foreground mt-1">
                {ach.stats.badges_earned} Badges Earned · {ach.stats.remaining} Labs Remaining · Final Certificate:{" "}
                <span className={ach.certificate.status === "Awarded" ? "text-success font-semibold" : "font-semibold"}>{ach.certificate.status}</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center"><div className="font-display font-bold text-3xl text-primary">{ach.stats.pct}%</div><div className="text-xs text-muted-foreground">Program Complete</div></div>
              <Button variant="outline" className="gap-2" onClick={() => navigate("/certificates")} data-testid="view-achievements-btn">
                <Trophy className="h-4 w-4" /> View Badges & Certificate
              </Button>
            </div>
          </div>
          <Progress value={ach.stats.pct} className="h-2.5 mt-4" />
        </div>
      )}

      {/* Class leaderboard */}
      {lb?.in_cohort && lb.entries?.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-6 mt-6" data-testid="dashboard-leaderboard">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="h-5 w-5 text-amber-500" />
            <h3 className="font-display font-bold text-lg">Class Leaderboard</h3>
            <span className="text-xs text-muted-foreground">{lb.cohort_name} · {lb.total_students} students</span>
            {lb.my_rank && <span className="ml-auto text-sm font-medium text-[#0078d4]" data-testid="lb-my-rank">You're #{lb.my_rank}</span>}
          </div>
          <div className="space-y-1.5">
            {lb.entries.slice(0, 8).map((e) => (
              <div key={e.id} className={`flex items-center gap-3 rounded-lg px-3 py-2 ${e.is_me ? "bg-[#0078d4]/10 border border-[#0078d4]/30" : "bg-secondary/40"}`} data-testid={`lb-row-${e.rank}`}>
                <span className={`h-7 w-7 rounded-full grid place-items-center text-xs font-bold ${e.rank === 1 ? "bg-amber-400 text-amber-950" : e.rank === 2 ? "bg-slate-300 text-slate-800" : e.rank === 3 ? "bg-orange-400 text-orange-950" : "bg-muted text-muted-foreground"}`}>{e.rank}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{e.name}{e.is_me && <span className="text-[#0078d4]"> (you)</span>}</div>
                  <div className="text-xs text-muted-foreground">{e.labs_completed}/{e.labs_total} labs · {e.badges} badge{e.badges === 1 ? "" : "s"}{e.exam_passed ? ` · exam ${e.exam_score}%` : ""}</div>
                </div>
                {e.certificate && <span className="text-[10px] uppercase tracking-wide bg-[#0078d4]/15 text-[#0078d4] px-2 py-0.5 rounded-full">Certified</span>}
                <span className="text-sm font-bold text-muted-foreground">{e.overall_pct}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Azure Cloud Engineer certificate + shareable link */}
      {azCert && (
        <div className="rounded-xl border border-[#0078d4]/30 bg-gradient-to-br from-[#0078d4]/5 to-transparent p-6 mt-6" data-testid="dashboard-azure-cert">
          <div className="flex items-start gap-4 flex-wrap">
            <div className={`h-12 w-12 rounded-xl grid place-items-center shrink-0 ${azCert.eligible ? "bg-[#0078d4] text-white" : "bg-secondary text-muted-foreground"}`}>
              <Award className="h-6 w-6" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Azure Cloud Engineer</div>
              <div className="font-display font-bold text-xl mt-0.5">
                {azCert.eligible ? "Certificate Earned 🎉" : "Hands-On Lab Certificate"}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {azCert.completed}/{azCert.total} Azure labs completed
                {azCert.exam?.passed && <span className="text-amber-600 font-medium"> · Practical Exam passed ({azCert.exam.best_score}%)</span>}
              </div>
              <Progress value={azCert.total ? (azCert.completed / azCert.total) * 100 : 0} className="h-2 mt-3 max-w-md" />

              {azCert.next_lab && !azCert.eligible && (
                <div className="mt-4 rounded-lg border border-[#0078d4]/25 bg-background/60 p-3 flex flex-wrap items-center gap-3" data-testid="dashboard-next-lab">
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] uppercase tracking-widest text-[#0078d4] font-semibold">
                      {azCert.next_lab.resuming ? "Resume where you left off" : "Recommended next lab"}
                    </div>
                    <div className="text-sm font-medium mt-0.5 truncate">{azCert.next_lab.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {azCert.next_lab.track_name} · {azCert.next_lab.difficulty}
                      {azCert.next_lab.est_min ? ` · ~${azCert.next_lab.est_min} min` : ""}
                      {" · "}{azCert.next_lab.track_remaining} to your {azCert.next_lab.track_name} badge
                    </div>
                  </div>
                  <Button size="sm" className="gap-2 bg-[#0078d4] hover:bg-[#106ebe] text-white shrink-0" data-testid="dashboard-next-lab-start"
                    onClick={() => window.open(`/azure?guided=${azCert.next_lab.id}`, "_blank", "noopener,noreferrer")}>
                    <ArrowRight className="h-4 w-4" /> {azCert.next_lab.resuming ? "Resume lab" : "Start lab"}
                  </Button>
                </div>
              )}

              {azCert.eligible && azCert.certificate ? (
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <Button size="sm" className="gap-2 bg-[#0078d4] hover:bg-[#106ebe] text-white" data-testid="az-cert-view"
                    onClick={() => window.open(`/verify/${azCert.certificate.certificate_id}`, "_blank", "noopener,noreferrer")}>
                    <ExternalLink className="h-4 w-4" /> View certificate
                  </Button>
                  <Button size="sm" variant="outline" className="gap-2" data-testid="az-cert-share"
                    onClick={() => {
                      const url = `${window.location.origin}/verify/${azCert.certificate.certificate_id}`;
                      navigator.clipboard?.writeText(url);
                      toast.success("Shareable verification link copied!");
                    }}>
                    <Copy className="h-4 w-4" /> Copy shareable link
                  </Button>
                  <code className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">{azCert.certificate.certificate_id}</code>
                </div>
              ) : (
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" className="gap-2" data-testid="az-cert-continue"
                    onClick={() => navigate("/guided")}><GraduationCap className="h-4 w-4" /> Continue guided labs</Button>
                  <Button size="sm" variant="outline" className="gap-2" data-testid="az-cert-exam"
                    onClick={() => navigate("/exam")}><Award className="h-4 w-4" /> Practical Exam</Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Assigned to you (from instructor cohorts) */}
      {assignments.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-6 mt-6" data-testid="assigned-to-you">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardList className="h-5 w-5 text-primary" />
            <h2 className="font-display font-bold text-lg">Assigned to you</h2>
            <Badge variant="secondary" className="ml-1">{assignments.filter((a) => !a.done).length} open</Badge>
          </div>
          <div className="space-y-2">
            {assignments.slice(0, 6).map((a) => {
              const st = a.done ? { c: "text-success", Icon: CheckCircle2, t: "Done" } : a.status === "overdue" ? { c: "text-red-500", Icon: AlertTriangle, t: "Overdue" } : { c: "text-muted-foreground", Icon: Clock, t: "Pending" };
              return (
                <div key={a.id} className="flex items-center justify-between rounded-md border border-border px-4 py-3" data-testid={`my-assignment-${a.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{a.title}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                      <Badge variant="outline" className="uppercase text-[10px]">{a.type}</Badge>
                      <span>{a.cohort_name}</span>
                      {a.due_at && <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3" />{new Date(a.due_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`text-xs flex items-center gap-1 ${st.c}`}><st.Icon className="h-3.5 w-3.5" />{st.t}</span>
                    {!a.done && (
                      <Button size="sm" variant="outline" className="gap-1" data-testid={`start-assignment-${a.id}`}
                        onClick={() => navigate(a.type === "quiz" ? `/quiz/${a.ref_id}` : `/lab/${a.ref_id}`)}>
                        Start <ArrowRight className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Level progress + recommended */}
      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 rounded-lg border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-display font-bold text-lg">Overall progress</h2>
            <span className="text-sm text-muted-foreground">{d.completion_pct}%</span>
          </div>
          <Progress value={d.completion_pct} className="h-2" />
          <div className="grid sm:grid-cols-2 gap-3 mt-6">
            {d.level_progress.map((lp) => (
              <div key={lp.id} className="flex items-center justify-between rounded-md border border-border px-4 py-3">
                <div>
                  <div className="text-xs text-muted-foreground">Level {lp.id}</div>
                  <div className="font-semibold text-sm">{lp.title}</div>
                </div>
                <span className="font-mono text-sm text-muted-foreground">{lp.done}/{lp.total}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-6 flex flex-col">
          <h2 className="font-display font-bold text-lg mb-3">Recommended next</h2>
          {d.recommended ? (
            <>
              <Badge className="w-fit mb-2" variant="secondary">{d.recommended.difficulty}</Badge>
              <div className="font-semibold">{d.recommended.title}</div>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-3 flex-1">{d.recommended.scenario}</p>
              <Button className="mt-4 w-full gap-2" data-testid="continue-learning" onClick={() => navigate(`/lab/${d.recommended.id}`)}>
                Start lab <ArrowRight className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <div className="text-sm text-muted-foreground flex-1 grid place-items-center">🎉 All labs completed!</div>
          )}
        </div>
      </div>

      {/* Badges */}
      <div className="rounded-lg border border-border bg-card p-6 mt-6">
        <h2 className="font-display font-bold text-lg mb-4">Achievements</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {d.all_badges.map((b) => (
            <div
              key={b.id}
              className={`flex items-center gap-3 rounded-md border p-3 ${b.earned ? "border-primary/40 bg-primary/5" : "border-border opacity-60"}`}
              data-testid={`badge-${b.id}`}
            >
              <div className={`h-10 w-10 rounded-md grid place-items-center ${b.earned ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
                {b.earned ? <Trophy className="h-5 w-5" /> : <Lock className="h-4 w-4" />}
              </div>
              <div>
                <div className="font-semibold text-sm">{b.name}</div>
                <div className="text-xs text-muted-foreground">{b.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* History */}
      {d.history.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-6 mt-6">
          <h2 className="font-display font-bold text-lg mb-4">Recent activity</h2>
          <div className="divide-y divide-border">
            {d.history.slice(0, 6).map((h) => (
              <div key={h.lab_id} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  <span className="text-sm font-medium">{h.title}</span>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="font-mono">+{h.xp} XP</span>
                  <span>Score {h.score}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
